import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Chip, CircularProgress,
  Avatar, Tooltip, TextField, Alert,
} from '@mui/material';
import CloseIcon           from '@mui/icons-material/Close';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon   from '@mui/icons-material/CalendarMonth';
import VideocamIcon        from '@mui/icons-material/Videocam';
import PersonIcon          from '@mui/icons-material/Person';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon     from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon    from '@mui/icons-material/ChevronRight';
import { getOrientadores, getSlotsOrientador, agendarSesionConMeet } from '../services/authApi';

// ── Helpers ───────────────────────────────────────────────────────────────
const DIAS_ES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const cotOffset = -5 * 60; // UTC-5 en minutos

function utcToCot(isoUtc) {
  const d = new Date(isoUtc);
  d.setMinutes(d.getMinutes() + cotOffset);
  return d;
}

function formatHora(isoUtc) {
  const d = utcToCot(isoUtc);
  return d.toISOString().slice(11, 16);
}

function formatFechaCorta(isoUtc) {
  const d = utcToCot(isoUtc);
  return `${DIAS_ES[d.getDay()]} ${d.getDate()} ${MESES_ES[d.getMonth()]}`;
}

// Agrupar slots por día COT
function agruparPorDia(slots) {
  const grupos = {};
  for (const s of slots) {
    const d = utcToCot(s.isoInicio);
    const clave = d.toISOString().slice(0, 10);
    if (!grupos[clave]) grupos[clave] = [];
    grupos[clave].push(s);
  }
  return grupos;
}

// ── Pasos ─────────────────────────────────────────────────────────────────
const PASO_ORIENTADOR = 1;
const PASO_SLOT       = 2;
const PASO_CONFIRMAR  = 3;
const PASO_EXITO      = 4;

// ── Componente principal ──────────────────────────────────────────────────
export default function AgendarSesionDialog({ open, onClose, afiliadoId, nombreAfiliado, emailAfiliado, onAgendado }) {
  const [paso,          setPaso]         = useState(PASO_ORIENTADOR);
  const [orientadores,  setOrientadores] = useState([]);
  const [orientador,    setOrientador]   = useState(null);
  const [slots,         setSlots]        = useState([]);
  const [slotSel,       setSlotSel]      = useState(null);
  const [diaActivo,     setDiaActivo]    = useState(null);
  const [notas,         setNotas]        = useState('');
  const [cargando,      setCargando]     = useState(false);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [error,         setError]        = useState(null);
  const [resultado,     setResultado]    = useState(null);
  const [semanaOffset,  setSemanaOffset] = useState(0); // 0=esta semana, 1=siguiente, etc.

  // Cargar orientadores al abrir
  useEffect(() => {
    if (!open) return;
    setPaso(PASO_ORIENTADOR);
    setOrientador(null); setSlots([]); setSlotSel(null);
    setDiaActivo(null); setNotas(''); setError(null); setResultado(null);
    setSemanaOffset(0);
    setCargando(true);
    getOrientadores()
      .then(data => setOrientadores(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudieron cargar los orientadores.'))
      .finally(() => setCargando(false));
  }, [open]);

  // Calcular la fecha de inicio de una semana dado el offset
  const getFechaInicioSemana = useCallback((offset) => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() + 1 + offset * 7); // mañana + offset semanas
    return hoy.toISOString().slice(0, 10); // yyyy-MM-dd
  }, []);

  // Cargar slots de una semana específica
  const cargarSlotsDeSemana = useCallback(async (ori, offset) => {
    setCargandoSlots(true);
    setSlotSel(null);
    setError(null);
    try {
      const fechaInicio = getFechaInicioSemana(offset);
      const data = await getSlotsOrientador(ori.id, 7, 60, fechaInicio);
      const slotsData = data.slots || [];
      setSlots(slotsData);
      const grupos = agruparPorDia(slotsData);
      const primerDia = Object.keys(grupos)[0] || null;
      setDiaActivo(primerDia);
    } catch {
      setError('No se pudo cargar el calendario. Verifique que el Calendar ID es correcto.');
    } finally {
      setCargandoSlots(false);
    }
  }, [getFechaInicioSemana]);

  // Cargar slots al seleccionar orientador
  const seleccionarOrientador = useCallback(async (ori) => {
    if (!ori.tieneCalendario) {
      setError(`${ori.nombre} no tiene calendario configurado. Configúrelo en el perfil del usuario.`);
      return;
    }
    setOrientador(ori);
    setSemanaOffset(0);
    setError(null);
    setCargando(true);
    try {
      const fechaInicio = getFechaInicioSemana(0);
      const data = await getSlotsOrientador(ori.id, 7, 60, fechaInicio);
      const slotsData = data.slots || [];
      setSlots(slotsData);
      const grupos = agruparPorDia(slotsData);
      const primerDia = Object.keys(grupos)[0] || null;
      setDiaActivo(primerDia);
      setPaso(PASO_SLOT);
    } catch {
      setError('No se pudo cargar el calendario. Verifique que el Calendar ID es correcto.');
    } finally {
      setCargando(false);
    }
  }, [getFechaInicioSemana]);

  // Navegar entre semanas
  const navegarSemana = useCallback((delta) => {
    const nuevoOffset = semanaOffset + delta;
    if (nuevoOffset < 0) return; // no ir al pasado
    setSemanaOffset(nuevoOffset);
    if (orientador) cargarSlotsDeSemana(orientador, nuevoOffset);
  }, [semanaOffset, orientador, cargarSlotsDeSemana]);

  // Agendar
  const confirmarAgendamiento = async () => {
    if (!slotSel || !orientador) return;
    setCargando(true);
    setError(null);
    try {
      const res = await agendarSesionConMeet({
        afiliadoId:  afiliadoId,
        orientadorId: orientador.id,
        inicioIso:   slotSel.isoInicio,
        duracionMin: 60,
        notas:       notas || null,
      });
      setResultado(res);
      setPaso(PASO_EXITO);
      if (onAgendado) onAgendado(res);
    } catch (e) {
      setError(e?.data?.mensaje || 'Error al agendar la sesión.');
    } finally {
      setCargando(false);
    }
  };

  const gruposDias = agruparPorDia(slots);
  const diasDisponibles = Object.keys(gruposDias).sort();
  const slotsDelDia = diaActivo ? (gruposDias[diaActivo] || []) : [];

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

      {/* Header */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
        color: '#fff', py: 2, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {paso > PASO_ORIENTADOR && paso < PASO_EXITO && (
            <IconButton size="small" onClick={() => { setPaso(p => p - 1); setError(null); }}
              sx={{ color: '#fff', mr: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <CalendarMonthIcon sx={{ fontSize: 20, opacity: 0.9 }} />
          <Box>
            <Typography fontWeight={700} fontSize="0.95rem">Agendar sesión de orientación</Typography>
            <Typography fontSize="0.72rem" sx={{ opacity: 0.8 }}>{nombreAfiliado}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Indicador de pasos */}
          {[1,2,3].map(p => (
            <Box key={p} sx={{
              width: 8, height: 8, borderRadius: '50%',
              background: paso >= p ? '#f0d070' : 'rgba(255,255,255,0.3)',
              transition: 'background 0.3s',
            }}/>
          ))}
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '24px !important', pb: 1, minHeight: 320 }}>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ── Paso 1: Seleccionar orientador ── */}
        {paso === PASO_ORIENTADOR && (
          <Box>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Selecciona el orientador que conducirá la sesión:
            </Typography>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={36} sx={{ color: '#2e7d32' }} />
              </Box>
            ) : orientadores.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                No hay orientadores configurados. Asigna el rol <strong>Orientador</strong> a un usuario primero.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {orientadores.map(ori => (
                  <Box key={ori.id}
                    onClick={() => seleccionarOrientador(ori)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 1.5, borderRadius: 2, cursor: ori.tieneCalendario ? 'pointer' : 'not-allowed',
                      border: '1px solid', borderColor: '#e0e0e0',
                      opacity: ori.tieneCalendario ? 1 : 0.5,
                      transition: 'all 0.15s',
                      '&:hover': ori.tieneCalendario ? {
                        borderColor: '#2e7d32', background: '#f0fdf0',
                        transform: 'translateX(4px)',
                      } : {},
                    }}>
                    <Avatar sx={{ bgcolor: '#1a4d2e', width: 38, height: 38, fontSize: '0.9rem' }}>
                      {(ori.nombre || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600} fontSize="0.875rem">{ori.nombre}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>{ori.email}</Typography>
                    </Box>
                    {ori.tieneCalendario ? (
                      <Chip label="Disponible" size="small"
                        sx={{ fontSize: '0.65rem', height: 20, background: '#e8f5e9', color: '#2e7d32' }} />
                    ) : (
                      <Tooltip title="Sin calendario configurado">
                        <Chip label="Sin calendario" size="small"
                          sx={{ fontSize: '0.65rem', height: 20, background: '#fff3e0', color: '#e65100' }} />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Paso 2: Selector de slots ── */}
        {paso === PASO_SLOT && (
          <Box>
            {/* Encabezado orientador */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PersonIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
              <Typography variant="body2" fontWeight={600}>{orientador?.nombre}</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>— selecciona fecha y hora</Typography>
            </Box>

            {/* Navegación de semanas */}
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              mb: 1.5, px: 0.5, py: 0.75, border:'1px solid #e0e0e0', borderRadius: 2,
              background: '#f8fdf8' }}>
              <IconButton size="small" onClick={() => navegarSemana(-1)}
                disabled={semanaOffset === 0 || cargandoSlots}
                sx={{ color: '#1a4d2e' }}>
                <ChevronLeftIcon/>
              </IconButton>
              <Box sx={{ textAlign:'center' }}>
                <Typography variant="caption" fontWeight={700} sx={{ color:'#1a4d2e', display:'block' }}>
                  {(() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 1 + semanaOffset * 7);
                    const fin = new Date(d);
                    fin.setDate(fin.getDate() + 6);
                    return `${d.getDate()} ${MESES_ES[d.getMonth()].slice(0,3)} — ${fin.getDate()} ${MESES_ES[fin.getMonth()].slice(0,3)} ${fin.getFullYear()}`;
                  })()}
                </Typography>
                <Typography variant="caption" sx={{ color:'#888', fontSize:'0.65rem' }}>
                  {semanaOffset === 0 ? 'Esta semana' : `Semana +${semanaOffset}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => navegarSemana(1)}
                disabled={cargandoSlots}
                sx={{ color: '#1a4d2e' }}>
                <ChevronRightIcon/>
              </IconButton>
            </Box>

            {(cargando || cargandoSlots) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={36} sx={{ color: '#2e7d32' }} />
              </Box>
            ) : diasDisponibles.length === 0 ? (
              <Box sx={{ textAlign:'center', py: 1 }}>
                <Alert severity="warning" sx={{ fontSize: '0.8rem', mb: 1.5 }}>
                  No hay disponibilidad esta semana para este orientador.
                </Alert>
                <Button size="small" variant="outlined" onClick={() => navegarSemana(1)}
                  endIcon={<ChevronRightIcon/>}
                  sx={{ color:'#1a4d2e', borderColor:'#1a4d2e', borderRadius:4 }}>
                  Ver semana siguiente
                </Button>
              </Box>
            ) : (
              <>
                {/* Selector de días */}
                <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 1, mb: 2 }}>
                  {diasDisponibles.map(dia => {
                    const d = utcToCot(dia + 'T13:00:00Z');
                    const activo = dia === diaActivo;
                    return (
                      <Box key={dia} onClick={() => { setDiaActivo(dia); setSlotSel(null); }}
                        sx={{
                          minWidth: 56, textAlign: 'center', cursor: 'pointer',
                          p: '8px 4px', borderRadius: 2, flexShrink: 0,
                          border: '1px solid', transition: 'all 0.15s',
                          borderColor: activo ? '#2e7d32' : '#e0e0e0',
                          background: activo ? '#1a4d2e' : '#fafafa',
                          '&:hover': { borderColor: '#2e7d32', background: activo ? '#1a4d2e' : '#f0fdf0' },
                        }}>
                        <Typography sx={{ fontSize: '0.62rem', color: activo ? 'rgba(255,255,255,0.7)' : '#888' }}>
                          {DIAS_ES[d.getDay()]}
                        </Typography>
                        <Typography fontWeight={700} sx={{ fontSize: '1.1rem', color: activo ? '#f0d070' : '#222', lineHeight: 1.2 }}>
                          {d.getDate()}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: activo ? 'rgba(255,255,255,0.6)' : '#aaa' }}>
                          {MESES_ES[d.getMonth()].slice(0, 3)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Slots del día */}
                <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block' }}>
                  Horarios disponibles — hora Colombia (COT):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {slotsDelDia.map((s, i) => {
                    const seleccionado = slotSel?.isoInicio === s.isoInicio;
                    return (
                      <Box key={i} onClick={() => setSlotSel(s)}
                        sx={{
                          px: 2, py: 0.75, borderRadius: 5, cursor: 'pointer',
                          border: '1.5px solid', transition: 'all 0.15s',
                          borderColor: seleccionado ? '#2e7d32' : '#e0e0e0',
                          background: seleccionado ? '#1a4d2e' : '#fff',
                          color: seleccionado ? '#f0d070' : '#333',
                          fontWeight: seleccionado ? 700 : 400,
                          fontSize: '0.82rem',
                          '&:hover': { borderColor: '#2e7d32', background: seleccionado ? '#1a4d2e' : '#f0fdf0' },
                        }}>
                        {formatHora(s.isoInicio)}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ── Paso 3: Confirmar ── */}
        {paso === PASO_CONFIRMAR && slotSel && (
          <Box>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Confirma los detalles antes de crear el evento en Google Calendar:
            </Typography>
            <Box sx={{ background: '#f8fdf8', border: '1px solid #c8e6c9', borderRadius: 2, p: 2, mb: 2 }}>
              {[
                ['👤 Afiliado',    nombreAfiliado],
                ['🎓 Orientador',  orientador?.nombre],
                ['📅 Fecha',       formatFechaCorta(slotSel.isoInicio)],
                ['⏰ Hora',        formatHora(slotSel.isoInicio) + ' (hora Colombia)'],
                ['⏱ Duración',    '60 minutos'],
                ['💻 Medio',       'Google Meet (enlace automático)'],
              ].map(([lbl, val]) => (
                <Box key={lbl} sx={{ display: 'flex', gap: 1, mb: 0.6 }}>
                  <Typography variant="caption" sx={{ color: '#888', minWidth: 120 }}>{lbl}:</Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#1a1a1a' }}>{val}</Typography>
                </Box>
              ))}
            </Box>
            <TextField fullWidth size="small" multiline rows={2}
              label="Notas adicionales (opcional)"
              value={notas}
              onChange={e => setNotas(e.target.value)}
              sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
              background: '#e3f2fd', borderRadius: 1.5, mt: 1 }}>
              <VideocamIcon sx={{ color: '#1565c0', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#1565c0' }}>
                Se creará automáticamente un evento en Google Calendar con enlace de Google Meet.
                El afiliado y el orientador recibirán la invitación por correo.
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Paso 4: Éxito ── */}
        {paso === PASO_EXITO && resultado && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1.5 }} />
            <Typography fontWeight={700} fontSize="1.1rem" sx={{ color: '#1a4d2e', mb: 1 }}>
              ¡Sesión agendada exitosamente!
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2.5 }}>
              El evento fue creado en Google Calendar y se enviaron las citaciones a todos los participantes.
            </Typography>
            {resultado.meetUrl && (
              <Box sx={{ background: '#e3f2fd', borderRadius: 2, p: 1.5, mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#1565c0', display: 'block', mb: 0.5, fontWeight: 600 }}>
                  🔗 Enlace Google Meet:
                </Typography>
                <Typography variant="caption" sx={{ color: '#1565c0', wordBreak: 'break-all' }}>
                  {resultado.meetUrl}
                </Typography>
              </Box>
            )}
            <Button variant="contained" onClick={onClose}
              sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius: 6 }}>
              Cerrar
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* Footer con acciones */}
      {paso !== PASO_EXITO && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={onClose} sx={{ color: '#888' }}>Cancelar</Button>
          {paso === PASO_SLOT && slotSel && (
            <Button variant="contained" onClick={() => setPaso(PASO_CONFIRMAR)}
              sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius: 6 }}>
              Continuar →
            </Button>
          )}
          {paso === PASO_CONFIRMAR && (
            <Button variant="contained"
              disabled={cargando}
              startIcon={cargando ? <CircularProgress size={14} color="inherit" /> : <VideocamIcon />}
              onClick={confirmarAgendamiento}
              sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius: 6 }}>
              {cargando ? 'Agendando...' : 'Confirmar y crear Meet'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
